import { Card, Descriptions, Button, Alert } from 'antd';
import { useParams, Link } from 'react-router-dom';
import { useReportPack } from './hooks/useReportPack';
import { useDownloadReportPack } from './hooks/useDownloadReportPack';
import { AccountingDisclaimer } from './shared/AccountingDisclaimer';
import { fmtCents } from './shared/formatCents';
import { ReportPackStatusTag } from './shared/ReportPackStatusTag';
import { REPORT_PACK_STATUS } from '../../../../../../constants/accounting';
import { DemoDataAlert } from '../../home/components/DemoDataAlert';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import { useIsPreActiveCustomer } from '../../../../../../hooks/useIsPreActiveCustomer';

const PAGE_CLASSES =
  'flex h-full w-full max-w-[1148px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden pb-4 pt-[16px]';

function BackToReportsLink() {
  return (
    <Link
      to=".."
      relative="path"
      aria-label="Back to all reports"
      style={{ fontSize: 18, lineHeight: 1 }}
    >
      ←
    </Link>
  );
}

export function ReportPackDetail() {
  const { reportPackId } = useParams<{ reportPackId: string }>();
  const { data, isLoading, isError } = useReportPack(reportPackId ?? '');
  const download = useDownloadReportPack();
  const isPreActive = useIsPreActiveCustomer();

  // The back link sits in the Card title on the branches that have no body, the
  // same move as ReportPackList: both of them used to render a permanent
  // skeleton with the only link out trapped inside the Card that never came.
  const fallbackTitle = (
    <div className="flex items-center gap-3">
      <BackToReportsLink />
      <span>Accounting Report</span>
    </div>
  );

  if (isLoading) {
    return (
      <div className={PAGE_CLASSES}>
        <Card title={fallbackTitle} loading />
      </div>
    );
  }

  // Not fetching and still no data: either the request failed, or the id
  // resolves to nothing — a stale bookmark, or a demo pack id from a year that
  // has since rolled over. `isLoading` is `isPending && isFetching`, so both of
  // those used to fall through to the skeleton above and spin forever.
  if (isError || !data) {
    return (
      <div className={PAGE_CLASSES}>
        <Card title={fallbackTitle}>
          <Alert
            type={isError ? 'error' : 'warning'}
            showIcon
            message={
              isError ? "We couldn't load this report" : 'Report not found'
            }
            description={
              isError
                ? 'Something went wrong fetching it. Try again in a moment.'
                : 'This report no longer exists, or the link is out of date.'
            }
          />
        </Card>
      </div>
    );
  }

  const status = data.status as string;
  const summary = (data.summary_json ?? {}) as any;
  const totals = summary.totals ?? {};

  return (
    <div className={PAGE_CLASSES}>
      {isPreActive && <DemoDataAlert />}

      <DemoDataWrapper showBadge={isPreActive}>
        <Card>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <BackToReportsLink />
              <h3 className="m-0 text-base font-semibold">
                {data.report_year} Accounting Report
              </h3>
            </div>
            {status === REPORT_PACK_STATUS.Ready && (
              // No ZIP exists behind a demo pack, and the download endpoint is
              // active-only.
              <Button
                type="primary"
                disabled={isPreActive}
                loading={download.isPending}
                onClick={() => download.mutate(data.report_pack_id)}
              >
                Download
              </Button>
            )}
          </div>
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="Status">
              <ReportPackStatusTag status={status} />
            </Descriptions.Item>
            <Descriptions.Item label="Generated">
              {data.created_at && new Date(data.created_at).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Entity Type">
              {data.business_type ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Disclaimer Version">
              {data.disclaimer_version ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Gross Revenue">
              {fmtCents(totals.gross_revenue)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Expenses">
              {fmtCents(totals.total_expenses)}
            </Descriptions.Item>
            <Descriptions.Item label="Net Profit">
              {fmtCents(totals.net_profit)}
            </Descriptions.Item>
            <Descriptions.Item label="Transactions">
              {summary.transaction_count ?? '—'}
            </Descriptions.Item>
          </Descriptions>

          {status === REPORT_PACK_STATUS.Failed && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 16 }}
              message="Generation failed"
            />
          )}

          {totals.uncategorized_count > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message={`${totals.uncategorized_count} transactions (${fmtCents(totals.uncategorized_total)}) were uncategorized when this report was generated.`}
            />
          )}

          <AccountingDisclaimer isPreActive={isPreActive} />
        </Card>
      </DemoDataWrapper>
    </div>
  );
}
